"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Organization → Manage subscription. Page title Usage & Plan Details",
  },
  {
    topic: "View, not checkout",
    rule: "There is no Buy / Upgrade button on this screen. Refresh reloads the same subscription. Plan changes for WhatsApp (when not on a custom plan) go to the account billing URL from WhatsApp config, not from here",
  },
  {
    topic: "What the table is",
    rule: "Each row is a plan feature: Plan Limit, Add-On, Total Limit, Usage (Used/Limit). Unlimited shows a check instead of a number. Used is how much of that feature the company has consumed",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Current Plan Information" },
      { heading: "2. Feature Usage Details" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function ManageSubscriptionPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Manage Subscription
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — see the current plan, renewal, and how much of each
            feature you have used
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Hub label <B>Manage subscription</B>. The screen is{" "}
            <B>Usage & Plan Details</B>. It answers: which plan are we on,
            when does it renew or end, and which limits are we hitting.
          </p>
          <Flow
            steps={["Settings", "Organization", "Manage subscription", "read plan + usage"]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Current Plan Information
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Header has <B>Refresh</B> to reload. Card fields:
          </p>
          <Definitions
            items={[
              { term: "Plan Name", desc: "The active Books plan" },
              { term: "Plan Start Date", desc: "When the current period started" },
              {
                term: "Auto Renewal",
                desc: "Enabled or Disabled",
              },
              {
                term: "Next date",
                desc: "Next Renewal Date if recurring; Trial End Date if a free trial; Plan End Date if it does not auto-renew",
              },
              {
                term: "What it affects",
                desc: "Nothing you edit here. When a period ends without renewal, the product can go view-only (you cannot create documents). Feature flags on Settings cards (WhatsApp, Incentive) follow this plan",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Feature Usage Details
          </h3>
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Feature, Plan Limit, Add-On, Total Limit, Usage (Used/Limit)",
              },
              {
                term: "What it affects",
                desc: "Create actions elsewhere hit these caps (users, documents, WhatsApp recipients, and the other named features on your plan). This table is how you see used vs allowed",
              },
            ]}
          />
          <Callout type="note">
            You do not buy add-ons from this page. It only reports plan limit
            plus any add-on already on the subscription.
          </Callout>
          <Screenshot caption="Usage & Plan Details — Current Plan Information and Feature Usage table, Refresh" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/whatsapp-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              WhatsApp Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Recipients cap comes from this plan
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/users"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Users <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Invites count against the user limit on the plan
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
